/**
 * @file components/chat/MessageUsageBadge.tsx
 * CHAT-07 — Badge FinOps discret affiché sous les bulles agent.
 *
 * ⚡ Script · 0 token          ← zero-token (script DSL), vert
 * 🏠 Local · 342 tok           ← modèle on-prem/local, bleu
 * 🏠 On-Prem · 342 tok         ← on-prem non qualifié, bleu
 * ☁️ Cloud · 1 240 tok · 0.0018€ ← cloud, gris
 *
 * Contrôles :
 * - VITE_SHOW_USAGE_BADGE=false → aucun badge (défaut : true)
 * - model_id visible en tooltip uniquement si renvoyé par le backend (ADMIN/ROOT)
 */
import React from 'react';
import type { UsageInfo } from './types.ts';

interface MessageUsageBadgeProps {
  usage: UsageInfo;
}

const TIER_ICON: Record<string, string> = {
  script: '⚡',
  local:  '🏠',
  onprem: '🏠',
  cloud:  '☁️',
};

const TIER_LABEL: Record<string, string> = {
  script: 'Script',
  local:  'Local',
  onprem: 'On-Prem',
  cloud:  'Cloud',
};

const TIER_CLASS: Record<string, string> = {
  script: 'text-green-600 dark:text-green-400',
  local:  'text-blue-600 dark:text-blue-400',
  onprem: 'text-blue-600 dark:text-blue-400',
  cloud:  'text-gray-500 dark:text-gray-400',
};

export default function MessageUsageBadge({ usage }: MessageUsageBadgeProps): React.ReactElement | null {
  // Masqué si VITE_SHOW_USAGE_BADGE est explicitement "false"
  if (import.meta.env.VITE_SHOW_USAGE_BADGE === 'false') return null;

  const tier = usage.model_tier ?? 'cloud';
  const icon  = TIER_ICON[tier]  ?? '🔵';
  const label = TIER_LABEL[tier] ?? tier;
  const cls   = TIER_CLASS[tier] ?? 'text-gray-500';

  const parts: string[] = [`${icon} ${label}`];

  if (usage.zero_token) {
    parts.push('· 0 token');
  } else {
    parts.push(`· ${usage.tokens_used.toLocaleString('fr-FR')} tok`);
    if (usage.cost_eur > 0) {
      parts.push(`· ${usage.cost_eur.toFixed(4)}€`);
    }
  }

  const text = parts.join(' ');

  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-mono mt-1 select-none ${cls}`}
      aria-label={`Usage FinOps : ${text}`}
    >
      {text}
      {/* model_id inline — présent uniquement pour ADMIN/ROOT (backend gate) */}
      {usage.model_id && (
        <span className="text-muted-foreground opacity-60 cursor-default">
          · {usage.model_id}
        </span>
      )}
    </span>
  );
}
